import styled, { keyframes } from 'styled-components';

export const VerificationWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 64px);
  background: ${({ theme }) => theme.landing.background};
  transition: background 0.3s ease;
`;

export const VerificationContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  margin-top: 64px;
  padding: ${({ theme }) => theme.landingSpacing.xl};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    padding: ${({ theme }) => theme.landingSpacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    padding: ${({ theme }) => theme.landingSpacing.md};
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.landingSpacing.xl};
  gap: ${({ theme }) => theme.landingSpacing.md};
  flex-wrap: wrap;

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const SectionTitle = styled.h1`
  font-weight: 500;
  font-size: 1.75rem;
  line-height: 1.2;
  margin: 0;
  color: ${({ theme }) => theme.landing.text};
  letter-spacing: -0.025em;
`;

export const SectionDescription = styled.p`
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin: 4px 0 0 0;
`;

export const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.landingSpacing.sm};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    flex-direction: column;
  }
`;

export const DashboardButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    border-color: ${({ theme }) => theme.landing.text};
  }
  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
  }
`;

export const GenerateReportButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};

  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.buttonHover};
    box-shadow: 0px 8px 16px rgba(37, 99, 235, 0.2);
  }
  &:disabled {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.textSecondary};
    border-color: ${({ theme }) => theme.landing.cardBorder};
    cursor: not-allowed;
  }

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.sm}) {
    width: 100%;
    justify-content: center;
  }
`;

export const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
`;

export const ProgressTrack = styled.div`
  flex: 1;
  height: 6px;
  background: ${({ theme }) => theme.landing.iconBg};
  border-radius: 99px;
  overflow: hidden;
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: var(--color-text-success, #22c55e);
  border-radius: 99px;
  width: ${({ $pct }) => $pct}%;
  transition: width 0.3s ease;
`;

export const ProgressCount = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.landing.textSecondary};
  font-weight: 600;
  white-space: nowrap;
`;

/* ---- card grid ---- */

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.landingSpacing.lg};

  @media (max-width: ${({ theme }) => theme.landingBreakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.landingBreakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  display: flex;
  flex-direction: column;
  gap: 10px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.text};
`;

export const EditIconButton = styled.button`
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  background: transparent;
  color: ${({ theme }) => theme.landing.textSecondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
    color: ${({ theme }) => theme.landing.buttonBg};
    border-color: ${({ theme }) => theme.landing.buttonBg};
  }
`;

export const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  font-size: 10.5px;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: 99px;
  letter-spacing: 0.02em;
  white-space: nowrap;

  ${({ $status }) =>
    $status === 'COMPLETE'
      ? `
        background: var(--color-background-success, rgba(34,197,94,0.12));
        color: var(--color-text-success, #22c55e);
      `
      : `
        background: var(--color-background-warning, rgba(245,158,11,0.12));
        color: var(--color-text-warning, #f59e0b);
      `}
`;

export const ConfidenceBadge = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 99px;
  background: rgba(37, 99, 235, 0.12);
  color: ${({ theme }) => theme.landing.buttonBg};

  ${({ $low }) =>
    $low &&
    `
      background: var(--color-background-warning, rgba(245,158,11,0.12));
      color: var(--color-text-warning, #f59e0b);
    `}
`;

export const CardMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.landing.textSecondary};
  line-height: 1.55;
`;

export const CardDocLink = styled.div`
  font-size: 12.5px;
  color: ${({ theme }) => theme.landing.textSecondary};
  display: flex;
  align-items: center;
  gap: 6px;
  word-break: break-all;
`;

/* ---- modal ---- */

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.15s ease;
  padding: ${({ theme }) => theme.landingSpacing.md};
`;

export const ModalBox = styled.div`
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.landing.cardBg};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.lg};
  padding: ${({ theme }) => theme.landingSpacing.lg};
  animation: ${slideUp} 0.18s ease;
`;

export const ModalTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${({ theme }) => theme.landing.text};
`;

export const ModalSubtitle = styled.p`
  margin: 0 0 ${({ theme }) => theme.landingSpacing.md};
  font-size: 13px;
  color: ${({ theme }) => theme.landing.textSecondary};
`;

export const ModalLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${({ theme }) => theme.landing.textSecondary};
  margin-bottom: 6px;
`;

export const ModalSelect = styled.select`
  width: 100%;
  background: ${({ theme }) => theme.landing.background};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  font-size: 0.95rem;
  color: ${({ theme }) => theme.landing.text};
  outline: none;
  box-sizing: border-box;
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};

  &:focus {
    border-color: ${({ theme }) => theme.landing.buttonBg};
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.landingSpacing.sm};
`;

export const ModalCancelButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.landing.text};
  border: 1px solid ${({ theme }) => theme.landing.cardBorder};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.landing.iconBg};
  }
`;

export const ModalSaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: 1px solid ${({ theme }) => theme.landing.buttonBg};
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.buttonHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/* ---- shared states ---- */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4rem 1rem;
  color: ${({ theme }) => theme.landing.textSecondary};
  font-size: 14px;

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const EmptyVerificationState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 3rem 1rem;
  text-align: center;
  color: ${({ theme }) => theme.landing.textSecondary};

  .spin {
    animation: ${spin} 1s linear infinite;
  }
`;

export const RetryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme }) => theme.landing.buttonBg};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.landingBorderRadius.md};
  padding: ${({ theme }) => theme.landingSpacing.sm} ${({ theme }) => theme.landingSpacing.md};
  cursor: pointer;
  margin-top: 4px;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.landing.buttonHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const GlobalError = styled.div`
  background: var(--color-background-danger);
  border: 0.5px solid var(--color-border-danger);
  border-radius: var(--border-radius-md);
  padding: 10px 14px;
  font-size: 13px;
  color: var(--color-text-danger);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: ${({ theme }) => theme.landingSpacing.lg};
`;

export const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-top: 8px;
  color: ${({ $type, theme }) =>
    $type === 'error' ? 'var(--color-text-danger)' : theme.landing.textSecondary};
`;
